import { Box, Paper, Slide, styled } from '@mui/material';
import cn from 'classnames';
import { Resizable } from 're-resizable';
import { Suspense, useEffect, useRef } from 'react';
import { DrawerWidths, MOBILE_BREAKPOINT } from 'src/config-global';
import { useActionBarState } from './useActionBarState';

type Props = {
  children: React.ReactNode;
  drawerSize: number;
};

export const ActionsDrawer = ({ children, drawerSize }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    isOpen,
    setIsOpen,
    reopen,
    openAction,
    setOpenAction,
    resizeWidth,
    setResizeWidth,
  } = useActionBarState();

  const handleOnExited = () => {
    setOpenAction(reopen);
    if (reopen) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (openAction) {
      setIsOpen(true);
    }
  }, [openAction, setIsOpen]);

  return (
    <ActionsDrawerContainer ref={containerRef} className={cn({ isOpen })}>
      <Resizable
        as={ResizableStyled}
        style={{ flexGrow: 1, height: '100%', overflow: 'hidden' }}
        onResizeStop={(_e, _direction, _ref, d) => {
          setResizeWidth(
            containerRef?.current?.offsetWidth || resizeWidth + d.width,
          );
        }}
        enable={{ left: true }}
        handleComponent={{
          left: <Handler />,
        }}
        size={{
          width: resizeWidth || drawerSize,
          height: '100%',
        }}
      >
        <Box
          flexGrow={1}
          width="100%"
          sx={{ height: '100%', overflow: 'hidden' }}
        >
          <Slide
            in={isOpen}
            direction="left"
            appear
            container={containerRef.current}
            onEntered={() =>
              containerRef.current?.classList.add('drawerShadow')
            }
            onExiting={() =>
              containerRef.current?.classList.remove('drawerShadow')
            }
            onExited={() => handleOnExited()}
          >
            <PaperStyled>
              <Suspense>{children}</Suspense>
            </PaperStyled>
          </Slide>
        </Box>
      </Resizable>
    </ActionsDrawerContainer>
  );
};

const ResizableStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100vw',
  [theme.breakpoints.up('sm')]: {
    minWidth: DrawerWidths.MEDIUM_WIDTH,
  },
}));

const Handler = styled('div')(({ theme }) => ({
  width: '4px',
  height: '100%',
  borderRight: `1px solid ${theme.palette.divider}`,
  cursor: 'ew-resize',
}));

const ActionsDrawerContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'row',
  top: 0,
  bottom: 0,
  right: 0,
  height: '100%',
  zIndex: 1000,
  overflow: 'hidden',
  pointerEvents: 'none',
  flexShrink: 0,

  '&.isOpen': {
    pointerEvents: 'auto',
  },
  '&.drawerShadow': {
    boxShadow: theme.customShadows.dialog,
  },

  [theme.breakpoints.up(MOBILE_BREAKPOINT)]: {
    bottom: 0,
    right: '61px',
  },
  [theme.breakpoints.up('xl')]: {
    position: 'relative',
    right: 0,
    '&:not(.isOpen)': {
      width: 0,
    },
  },
}));

const PaperStyled = styled(Paper)(({ theme }) => ({
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  borderRadius: 0,
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));
